import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import HeroSection from './components/HeroSection';
import CategoryTabs from './components/CategoryTabs';
import MovieGrid from './components/MovieGrid';
import FeatureSection from './components/FeatureSection';
import { movies } from '@/mocks/movies';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const newMovies = movies.filter((m) => m.isNew).slice(0, 6);
  const hotMovies = movies.filter((m) => m.isHot).slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <HeroSection />

        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                  分类浏览
                </h2>
              </div>
              <CategoryTabs
                activeCategory={activeCategory}
                onCategoryChange={(slug) => {
                  setActiveCategory(slug);
                }}
              />
              <div className="mt-6">
                <MovieGrid categorySlug={activeCategory} limit={12} />
              </div>
              <div className="mt-8 text-center">
                <Link
                  to={`/category/${activeCategory}`}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-primary-400 border border-primary-500/30 rounded-full hover:bg-primary-500/10 transition-all duration-300 whitespace-nowrap"
                >
                  查看更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                    最新更新
                  </h2>
                </div>
                <Link
                  to="/category/action"
                  className="text-sm text-foreground-500 hover:text-primary-400 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                {newMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/movie/${movie.id}`}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={movie.image}
                        alt={`${movie.title} 电影海报`}
                        title={`${movie.title} 最新高清电影`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-accent-500 text-background-950 rounded-md whitespace-nowrap">
                          NEW
                        </span>
                      </div>
                      <div className="absolute top-2 left-2">
                        <div className="flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-md px-2 py-0.5">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px] text-accent-500"></i>
                          </div>
                          <span className="text-[11px] font-medium text-accent-400">{movie.rating}</span>
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 p-3">
                        <h3 className="text-sm font-medium text-white leading-tight line-clamp-2 group-hover:text-primary-300 transition-colors">
                          {movie.title}
                        </h3>
                        <span className="text-[11px] text-foreground-400 mt-1 block">{movie.year}</span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-white shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                          <i className="ri-play-fill text-xl ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-100 font-heading">
                    热门推荐
                  </h2>
                </div>
                <Link
                  to="/category/drama"
                  className="text-sm text-foreground-500 hover:text-primary-400 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
                {hotMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/movie/${movie.id}`}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={movie.image}
                        alt={`${movie.title} 电影海报`}
                        title={`${movie.title} 热门推荐电影`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-white rounded-md whitespace-nowrap">
                          {movie.quality}
                        </span>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 p-3">
                        <h3 className="text-sm font-medium text-white leading-tight line-clamp-2 group-hover:text-accent-300 transition-colors">
                          {movie.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] text-foreground-400">{movie.year}</span>
                          <span className="text-[11px] text-accent-400 flex items-center gap-0.5">
                            <i className="ri-star-fill text-[10px]"></i>
                            {movie.rating}
                          </span>
                        </div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-white shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                          <i className="ri-play-fill text-xl ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <FeatureSection />
      </main>

      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '6080影视 - 免费高清电影在线观看',
            description:
              '6080影视依托6080新视觉影视资源，提供免费高清电影在线观看服务，海量电影大全覆盖最新大片、经典佳作。',
            url: 'https://yunlv8888.cn/',
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://yunlv8888.cn/',
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}